/**
 * « Activite du compte » (« Security logs » de Proton, « Activite recente » de
 * Google) : lecture, cote PWA, du journal de securite que le serveur garde 90 j
 * pour le compte (`GET /v1/accounts/security-events`).
 *
 * Le journal porte l'IP et le `User-Agent` de QUI a frappe a la porte -- y
 * compris un tiers qui essaie le mot de passe : ce sont des chaines choisies
 * par un attaquant. Ce module ne rend donc que du TEXTE (l'appelant l'echappe),
 * valide chaque ligne recue et jette ce qu'il ne reconnait pas, et resume le
 * navigateur par une liste fermee de noms plutot que de recopier l'en-tete.
 */

export type SecurityLogResult = 'succes' | 'echec' | 'exigee';

export interface SecurityLogEvent {
  readonly horodatage: number;
  readonly evenement: string;
  readonly resultat: SecurityLogResult;
  readonly ip?: string;
  readonly userAgent?: string;
}

/** Libelles des evenements du serveur (`SECURITY_EVENT_TYPES`, apps/server/src/security-events.ts). */
const EVENT_LABELS: Readonly<Record<string, string>> = {
  inscription: 'Creation du compte',
  connexion: 'Connexion',
  mfa: 'Second facteur',
  facteur_ajoute: 'Cle de securite ajoutee',
  facteur_retire: 'Cle de securite retiree',
  cle_recuperation_activee: 'Cle de recuperation activee',
  recuperation: 'Recuperation du compte',
  changement_mot_de_passe_maitre: 'Changement du mot de passe maitre',
  changement_email: 'Changement de l’adresse e-mail',
  urgence_demande: 'Demande d’acces d’urgence',
  urgence_refus: 'Acces d’urgence refuse',
  urgence_octroi: 'Acces d’urgence accorde',
  mode_voyage_active: 'Mode voyage active',
  mode_voyage_desactive: 'Mode voyage desactive',
  sessions_revoquees: 'Deconnexion de tous les appareils',
  session_revoquee: 'Deconnexion d’un appareil',
  liaison_idenva: 'Compte Idenva lie',
  deliaison_idenva: 'Compte Idenva delie',
  deconnexion_idenva: 'Deconnexion depuis Idenva',
  suppression_compte: 'Suppression du compte',
};

const RESULT_LABELS: Readonly<Record<SecurityLogResult, string>> = {
  succes: 'Reussi',
  echec: 'Echoue',
  exigee: 'Second facteur demande',
};

export function securityEventLabel(evenement: string): string {
  return Object.hasOwn(EVENT_LABELS, evenement) ? EVENT_LABELS[evenement]! : 'Evenement de securite';
}

export function securityResultLabel(resultat: SecurityLogResult): string {
  return RESULT_LABELS[resultat];
}

/** Une IPv6 textuelle tient en 45 caracteres (meme borne que le serveur). */
const MAX_IP_LENGTH = 45;
const MAX_USER_AGENT_LENGTH = 128;
const IP_SHAPE = /^[0-9A-Fa-f:.]+$/u;

function isResult(value: unknown): value is SecurityLogResult {
  return value === 'succes' || value === 'echec' || value === 'exigee';
}

/**
 * Valide la reponse du serveur ligne a ligne : une ligne illisible est jetee,
 * jamais affichee a moitie. Une IP qui n'a pas la forme d'une adresse est
 * omise ; un navigateur trop long est coupe. Le resultat est trie du plus
 * recent au plus ancien, quel que soit l'ordre recu.
 */
export function parseSecurityLog(body: unknown): SecurityLogEvent[] {
  const raw = (body as { events?: unknown } | null | undefined)?.events;
  if (!Array.isArray(raw)) throw new Error('journal de securite illisible');
  const events: SecurityLogEvent[] = [];
  for (const item of raw) {
    if (typeof item !== 'object' || item === null) continue;
    const { horodatage, evenement, resultat, ip, userAgent } = item as Record<string, unknown>;
    if (typeof horodatage !== 'number' || !Number.isFinite(horodatage)) continue;
    if (typeof evenement !== 'string' || evenement.length === 0 || evenement.length > 64) continue;
    if (!isResult(resultat)) continue;
    const event: { -readonly [K in keyof SecurityLogEvent]: SecurityLogEvent[K] } = { horodatage, evenement, resultat };
    if (typeof ip === 'string' && ip.length > 0 && ip.length <= MAX_IP_LENGTH && IP_SHAPE.test(ip)) event.ip = ip;
    if (typeof userAgent === 'string' && userAgent.length > 0) event.userAgent = userAgent.slice(0, MAX_USER_AGENT_LENGTH);
    events.push(event);
  }
  return events.sort((a, b) => b.horodatage - a.horodatage);
}

/** Ordre significatif : Edge et Opera se declarent aussi « Chrome », Chrome se declare aussi « Safari ». */
const BROWSERS: readonly (readonly [RegExp, string])[] = [
  [/\bEdg(?:e|A|iOS)?\//u, 'Edge'],
  [/\b(?:OPR|Opera)\//u, 'Opera'],
  [/\bSamsungBrowser\//u, 'Samsung Internet'],
  [/\b(?:Firefox|FxiOS)\//u, 'Firefox'],
  [/\b(?:Chrome|CriOS|Chromium)\//u, 'Chrome'],
  [/\bVersion\/[\d.]+.*\bSafari\//u, 'Safari'],
];

/** Ordre significatif : Android se declare aussi « Linux », iOS aussi « Mac OS X ». */
const SYSTEMS: readonly (readonly [RegExp, string])[] = [
  [/\bAndroid\b/u, 'Android'],
  [/\b(?:iPhone|iPad|iPod)\b/u, 'iOS'],
  [/\bWindows\b/u, 'Windows'],
  [/\bMac OS X\b|\bMacintosh\b/u, 'macOS'],
  [/\bCrOS\b/u, 'ChromeOS'],
  [/\bLinux\b/u, 'Linux'],
];

/**
 * « Firefox sur Linux » a partir d'un `User-Agent`, en ne rendant QUE des noms
 * de cette liste fermee : un en-tete forge ne peut rien faire afficher d'autre.
 */
export function describeUserAgent(userAgent: string | undefined): string {
  if (userAgent === undefined || userAgent.length === 0) return 'Appareil non renseigne';
  const browser = BROWSERS.find(([pattern]) => pattern.test(userAgent))?.[1];
  const system = SYSTEMS.find(([pattern]) => pattern.test(userAgent))?.[1];
  if (browser !== undefined && system !== undefined) return `${browser} sur ${system}`;
  if (browser !== undefined) return browser;
  if (system !== undefined) return `Navigateur inconnu sur ${system}`;
  return 'Client non reconnu';
}

/** Une connexion ratee ou une recuperation ratee : ce qu'un titulaire doit remarquer. */
export function isSuspiciousEvent(event: SecurityLogEvent): boolean {
  return event.resultat === 'echec' && (event.evenement === 'connexion' || event.evenement === 'mfa' || event.evenement === 'recuperation');
}

export interface SecurityLogSummary {
  /** Connexions, seconds facteurs ou recuperations ratees sur la fenetre. */
  readonly failures: number;
  /** Adresses IP distinctes d'ou viennent ces echecs. */
  readonly failureSources: number;
}

/** Fenetre de l'alerte : un echec vieux de trois mois ne merite plus un bandeau. */
export const SECURITY_LOG_ALERT_WINDOW_MS = 30 * 24 * 60 * 60 * 1000;

export function summarizeSecurityLog(events: readonly SecurityLogEvent[], now: number = Date.now()): SecurityLogSummary {
  const since = now - SECURITY_LOG_ALERT_WINDOW_MS;
  const failed = events.filter((event) => event.horodatage >= since && isSuspiciousEvent(event));
  return { failures: failed.length, failureSources: new Set(failed.map((event) => event.ip ?? '?')).size };
}
